import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { requestPush, getPushSubscription, unsubscribePush, pushSupported } from "@/hooks/useNotifications";
import { BellRing, BellOff, Send, Loader2, Smartphone, CheckCircle2 } from "lucide-react";

export function PushCard() {
  const [subscribed, setSubscribed] = useState(false);
  const [devices, setDevices] = useState(0);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setSubscribed(!!(await getPushSubscription()));
    try { const { data } = await api.get("/push/subscriptions"); setDevices(data.count); } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const enable = async () => {
    setBusy(true);
    const r = await requestPush();
    if (r === "granted") toast.success("Push ativado! Você receberá alertas mesmo com o navegador fechado.");
    else if (r === "unsupported" || r === "no-sw") toast.error("Este navegador não suporta push. No iPhone, adicione o FlowIA à Tela de Início primeiro.");
    else if (r === "denied") toast.error("Permissão bloqueada. Libere as notificações nas configurações do navegador.");
    else if (r === "disabled") toast.error("Push não configurado no servidor.");
    else toast.error("Não foi possível ativar o push.");
    await load(); setBusy(false);
  };
  const disable = async () => { setBusy(true); await unsubscribePush(); toast.info("Push desativado neste dispositivo."); await load(); setBusy(false); };
  const test = async () => {
    setBusy(true);
    try { const { data } = await api.post("/push/test"); data.sent > 0 ? toast.success(`Notificação de teste enviada para ${data.sent} dispositivo(s).`) : toast.error("Nenhum dispositivo inscrito."); }
    catch { toast.error("Falha ao enviar o teste."); }
    setBusy(false);
  };

  return (
    <div className="glow-card rounded-2xl p-5" data-testid="dashboard-web-push-toggle">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-cyan-300"><BellRing className="h-5 w-5" /><h3 className="font-display text-lg font-semibold text-white">Web Push</h3></div>
        <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[11px] font-medium ${subscribed ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-300" : "border-slate-700 bg-slate-900/50 text-slate-400"}`} data-testid="push-device-status">
          {subscribed ? <><CheckCircle2 className="h-3 w-3" /> Ativo neste aparelho</> : "Inativo aqui"}
        </span>
      </div>
      <p className="mt-2 text-sm text-slate-400">Alertas de novas mensagens, agendamentos e cancelamentos direto no celular — mesmo com o navegador fechado.</p>
      <p className="mt-2 flex items-center gap-1.5 text-xs text-slate-500" data-testid="push-devices-count"><Smartphone className="h-3.5 w-3.5" /> {devices} dispositivo(s) inscrito(s) nesta empresa</p>
      <div className="mt-4 flex flex-wrap gap-2">
        {subscribed ? (
          <>
            <Button onClick={test} disabled={busy} className="bg-cyan-400 font-semibold text-slate-950 hover:bg-cyan-300" data-testid="push-test-btn">{busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Send className="mr-1.5 h-4 w-4" />} Enviar teste</Button>
            <Button onClick={disable} disabled={busy} variant="outline" className="border-slate-700 bg-slate-900/50 text-slate-300" data-testid="push-disable-btn"><BellOff className="mr-1.5 h-4 w-4" /> Desativar</Button>
          </>
        ) : (
          <Button onClick={enable} disabled={busy || !pushSupported()} className="w-full bg-cyan-400 font-semibold text-slate-950 hover:bg-cyan-300" data-testid="push-enable-btn">{busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <BellRing className="mr-1.5 h-4 w-4" />} Ativar notificações</Button>
        )}
      </div>
      {!pushSupported() && <p className="mt-2 text-xs text-amber-300">Navegador sem suporte a push. No iPhone: Safari → Compartilhar → Adicionar à Tela de Início.</p>}
    </div>
  );
}
